'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import PreviewCard from './PreviewCard';
import ColorThemePicker from './ColorThemePicker';
import ShareSection from './ShareSection';
import { createClient } from '@/lib/supabase/client';
import { Heart, Sparkles, ChevronRight, Save, CheckCircle } from 'lucide-react';

export type ColorTheme = 'classic' | 'pastel' | 'bold' | 'sunset' | 'mint' | 'olive-lilac' | 'golden-silver';

export interface EventConfig {
  parentName1: string;
  parentName2: string;
  babyNameHint: string;
  dueDate: string;
  customMessage: string;
  colorTheme: ColorTheme;
  eventTitle: string;
}

const defaultValues: EventConfig = {
  parentName1: '',
  parentName2: '',
  babyNameHint: '',
  dueDate: '',
  customMessage: '¡Ayúdanos a descubrir si es niño o niña! 💙🩷',
  colorTheme: 'classic',
  eventTitle: 'Revelación de Género',
};

function generateToken(): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let token = '';
  for (let i = 0; i < 20; i++) {
    token += chars[Math.floor(Math.random() * chars.length)];
  }
  return token;
}

export default function EventSetupClient() {
  const [saved, setSaved] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [organizerUrl, setOrganizerUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState<'setup' | 'share'>('setup');
  const [loadError, setLoadError] = useState('');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EventConfig>({ defaultValues });

  const watchedValues = watch();

  useEffect(() => {
    const loadEvent = async () => {
      // Try to load from Supabase using stored token
      const storedToken = localStorage.getItem('babyreveal_organizer_token');
      if (storedToken) {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .eq('token', storedToken)
          .maybeSingle();

        if (!error && data) {
          setValue('parentName1', data.parent_name1 || '');
          setValue('parentName2', data.parent_name2 || '');
          setValue('babyNameHint', data.baby_name_hint || '');
          setValue('dueDate', data.due_date || '');
          setValue('customMessage', data.custom_message || '');
          setValue('colorTheme', (data.color_theme as ColorTheme) || 'classic');
          setValue('eventTitle', data.event_title || 'Revelación de Género');

          if (typeof window !== 'undefined') {
            const base = window.location.origin;
            setShareUrl(`${base}/participation-screen?t=${storedToken}`);
            setOrganizerUrl(`${base}/organizer/${storedToken}`);
          }
          return;
        }
      }

      // Fallback: check localStorage for legacy data
      const stored = localStorage.getItem('babyreveal_event');
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as EventConfig;
          Object.entries(parsed).forEach(([key, value]) => {
            setValue(key as keyof EventConfig, value);
          });
        } catch {
          // ignore
        }
      }

      if (typeof window !== 'undefined') {
        setShareUrl(`${window.location.origin}/participation-screen`);
      }
    };

    loadEvent();
  }, [setValue]);

  const onSubmit = async (data: EventConfig) => {
    setSaving(true);
    setLoadError('');

    try {
      const supabase = createClient();

      // Get or create organizer token
      let token = localStorage.getItem('babyreveal_organizer_token');
      if (!token) {
        token = generateToken();
        localStorage.setItem('babyreveal_organizer_token', token);
      }

      // Upsert event in Supabase
      const { error } = await supabase
        .from('events')
        .upsert(
          {
            token,
            parent_name1: data.parentName1,
            parent_name2: data.parentName2,
            baby_name_hint: data.babyNameHint,
            due_date: data.dueDate,
            custom_message: data.customMessage,
            color_theme: data.colorTheme,
            event_title: data.eventTitle,
          },
          { onConflict: 'token' }
        );

      if (error) {
        console.error('Error saving event:', error.message);
        setLoadError('Error al guardar. Intenta de nuevo.');
        setSaving(false);
        return;
      }

      if (typeof window !== 'undefined') {
        const base = window.location.origin;
        setShareUrl(`${base}/participation-screen?t=${token}`);
        setOrganizerUrl(`${base}/organizer/${token}`);
      }

      setSaving(false);
      setSaved(true);
      setCurrentStep('share');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error('Unexpected error:', err);
      setLoadError('Error inesperado. Intenta de nuevo.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-hero-gradient hearts-bg">
      {/* Header */}
      <header className="w-full px-4 py-4 flex items-center justify-between max-w-screen-xl mx-auto">
        <div className="flex items-center gap-3">
          <AppLogo size={36} />
          <span className="font-display text-xl font-700 text-foreground tracking-tight">
            BabyReveal
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/results-reveal-screen"
            className="flex items-center gap-1.5 text-sm font-600 text-muted-foreground hover:text-foreground transition-colors px-3 py-1.5 rounded-full hover:bg-muted"
          >
            Ver resultados
            <ChevronRight size={14} />
          </Link>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-4 pb-16">
        {/* Hero */}
        <div className="text-center pt-6 pb-10">
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm border border-border rounded-full px-4 py-1.5 mb-4 card-shadow">
            <Sparkles size={14} className="text-primary" />
            <span className="text-xs font-600 text-muted-foreground tracking-wide uppercase">
              Crea tu evento
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-800 text-foreground mb-3">
            ¿Niño{' '}
            <span className="text-gradient-blue">💙</span>
            {' '}o Niña{' '}
            <span className="text-gradient-pink">🩷</span>?
          </h1>
          <p className="text-muted-foreground text-lg max-w-md mx-auto">
            Configura tu revelación de género y comparte el enlace con tus seres queridos.
          </p>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <button
            onClick={() => setCurrentStep('setup')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600 transition-all duration-200 ${
              currentStep === 'setup' ?'bg-primary text-white card-shadow-pink' :'bg-white/60 text-muted-foreground hover:bg-white'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-xs font-700">1</span>
            Configura
          </button>
          <div className="w-8 h-px bg-border" />
          <button
            onClick={() => setCurrentStep('share')}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600 transition-all duration-200 ${
              currentStep === 'share' ?'bg-secondary text-white card-shadow-blue' :'bg-white/60 text-muted-foreground hover:bg-white'
            }`}
          >
            <span className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-xs font-700">2</span>
            Comparte
          </button>
        </div>

        <div className="grid grid-cols-1 gap-8 items-start">
          {/* Left: Form */}
          <div className={`${currentStep === 'setup' ? 'block' : 'hidden lg:block'}`}>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Event Details */}
              <div className="bg-card rounded-2xl border border-border card-shadow p-6 mb-4">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-full bg-girl-gradient flex items-center justify-center">
                    <Heart size={14} className="text-white" />
                  </div>
                  <h2 className="text-base font-700 text-foreground">Detalles del Evento</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1">
                      Título del evento
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Aparecerá en la pantalla de votación
                    </p>
                    <input
                      {...register('eventTitle', { required: 'El título es requerido' })}
                      type="text"
                      placeholder="ej. Revelación de los García"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                    />
                    {errors.eventTitle && (
                      <p className="mt-1 text-xs text-red-500">{errors.eventTitle.message}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1">
                        Nombre (Mamá)
                      </label>
                      <input
                        {...register('parentName1', { required: 'Requerido' })}
                        type="text"
                        placeholder="ej. Sofía"
                        className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                      />
                      {errors.parentName1 && (
                        <p className="mt-1 text-xs text-red-500">{errors.parentName1.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-600 text-foreground mb-1">
                        Nombre (Papá)
                      </label>
                      <input
                        {...register('parentName2', { required: 'Requerido' })}
                        type="text"
                        placeholder="ej. Mateo"
                        className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                      />
                      {errors.parentName2 && (
                        <p className="mt-1 text-xs text-red-500">{errors.parentName2.message}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1">
                      Fecha probable
                    </label>
                    <p className="text-xs text-muted-foreground mb-2">Fecha estimada de parto</p>
                    <input
                      {...register('dueDate')}
                      type="date"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-600 text-foreground mb-1">
                      Mensaje para los participantes
                    </label>
                    <textarea
                      {...register('customMessage', { required: 'El mensaje es requerido' })}
                      rows={2}
                      placeholder="ej. ¡Adivina si es niño o niña y celebra con nosotros!"
                      className="w-full px-4 py-2.5 rounded-xl border border-border bg-input text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none"
                    />
                    {errors.customMessage && (
                      <p className="mt-1 text-xs text-red-500">{errors.customMessage.message}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Color Theme */}
              <div className="bg-card rounded-2xl border border-border card-shadow p-6 mb-4">
                <ColorThemePicker
                  value={watchedValues.colorTheme}
                  onChange={(theme) => setValue('colorTheme', theme)}
                />
              </div>

              {loadError && (
                <p className="mb-3 text-sm text-red-500 text-center">{loadError}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={saving}
                className="w-full py-3.5 rounded-2xl bg-girl-gradient text-white font-700 text-base flex items-center justify-center gap-2 card-shadow-pink hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-70"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Guardando...
                  </>
                ) : saved ? (
                  <>
                    <CheckCircle size={18} />
                    ¡Guardado! Ahora comparte
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Guardar y Generar Enlace
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right: Preview + Share */}
          <div className={`space-y-4 ${currentStep === 'share' ? 'block' : 'hidden lg:block'}`}>
            {/* Live Preview */}
            <div className="bg-card rounded-2xl border border-border card-shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles size={16} className="text-primary" />
                <h3 className="text-sm font-700 text-foreground">Vista previa en vivo</h3>
              </div>
              <PreviewCard config={watchedValues} />
            </div>

            {/* Share Section */}
            <ShareSection shareUrl={shareUrl} organizerUrl={organizerUrl} config={watchedValues} />
          </div>
        </div>
      </main>
    </div>
  );
}